//
// This component renders a date range on a form (i.e. start and end date fields).
//
/* eslint-disable max-depth */
/* eslint-disable react/jsx-no-bind */
import React, { memo, useCallback, useContext, useMemo } from 'react';

import PropTypes from 'prop-types';

import moment from 'moment-timezone';

import { useDispatch, useSelector } from 'react-redux';

import { updateDataField } from '@actions/data-detail-actions';

import ParamsContext from '@components/entity/info/params-context';
import DrawerSectionTitle from '@components/public/drawer-section-title';

import { detailEdit } from '@constants/mui-theme';

import { Checkbox } from '@mui';

import { getMetadataForType } from '@selectors/forms-selector';

import FormattedDatePicker from '@shared/formatted-date-picker';
import MomentTimePicker from '@shared/moment-time-picker';

import './forms.scss';

const PICKER_PROPS = { floatingLabelStyle: { whiteSpace: 'nowrap' } };

const getTimeLabel = label => {
  const text = label?.replace('date', 'time')?.replace('Date', 'Time');
  if (text?.search('time') < 0 && text?.search('Time') < 0) {
    return `${text} time`;
  }
  return text;
};

const uniqueErrors = errors => {
  if (Array.isArray(errors)) {
    return [...new Set(errors)].join(', ');
  }
  return null;
};

const DateRange = ({ fieldNames, isPublic, readOnly }) => {
  const dispatch = useDispatch();
  const { dataType } = useContext(ParamsContext);
  const { data, error: errors } = useSelector(state => state.dataDetail);
  const metadata = useSelector(state => getMetadataForType(state, dataType, isPublic));

  const onChange = useCallback((field, value) => {
    dispatch(updateDataField(field, value));
  }, [dispatch]);

  const onBoolChange = useCallback((field, value) => {
    if (value !== data[field]) {
      dispatch(updateDataField(field, value));
    }
  }, [data, dispatch]);

  const onCheck = useCallback(event => {
    const name = event.target.name;
    const newValue = !data[name];
    onBoolChange(name, newValue);
  }, [data, onBoolChange]);

  const onStartDateChange = useCallback(value => {
    if (value) {
      // Preserve the time part from the existing start_date
      const existingTime = data.start_date ? moment(data.start_date) : null;
      const newDate = existingTime ?
        moment(value).set({
          hour: existingTime.hour(),
          minute: existingTime.minute()
        }) :
        moment(value).set({ hour: '00', minute: '00' });
      onChange('start_date', newDate);
      if (!data.end_date) {
        onChange('end_date', newDate);
      }
    } else {
      onChange('start_date', null);
    }
  }, [data.end_date, data.start_date, onChange]);

  const onEndDateChange = useCallback(value => {
    if (value) {
      // Preserve the time part from the existing end_date
      const existingTime = data.end_date ? moment(data.end_date) : null;
      const newDate = existingTime ?
        moment(value).set({
          hour: existingTime.hour(),
          minute: existingTime.minute()
        }) :
        moment(value).set({ hour: '00', minute: '00' });
      onChange('end_date', newDate);
    } else {
      onChange('end_date', null);
    }
  }, [data.end_date, onChange]);

  const onStartTimeChange = useCallback(value => {
    onChange('start_time', value);
  }, [onChange]);

  const onEndTimeChange = useCallback(value => {
    onChange('end_time', value);
  }, [onChange]);

  const timeVisible = useMemo(() => fieldNames.includes('all_day') && !data.all_day, [data.all_day, fieldNames]);
  const startStyle = useMemo(() => timeVisible ? detailEdit.columnStyles.startDateRangeDate : detailEdit.columnStyles.col50, [timeVisible]);
  const endStyle = useMemo(() => timeVisible ? detailEdit.columnStyles.endDateRangeDate : detailEdit.columnStyles.col50, [timeVisible]);

  // Memoize some picker component properties.
  const startDateError = useMemo(() => uniqueErrors(errors.start_date), [errors.start_date]);
  const startTimeError = useMemo(() => uniqueErrors(errors.start_time), [errors.start_time]);
  const endDateError = useMemo(() => uniqueErrors(errors.end_date), [errors.end_date]);
  const endTimeError = useMemo(() => uniqueErrors(errors.end_time), [errors.end_time]);

  const startDateValue = useMemo(() => data?.start_date ? moment(data?.start_date).toDate() : null, [data?.start_date]);
  const endDateValue = useMemo(() => data?.end_date ? moment(data?.end_date).toDate() : null, [data?.end_date]);
  const startTimeValue = useMemo(() => data.start_time || (data.start_date ? moment(data.start_date) : null), [data.start_date, data.start_time]);
  const endTimeValue = useMemo(() => data.end_time || (data.end_date ? moment(data.end_date) : null), [data.end_date, data.end_time]);

  const startDateLabel = useMemo(
    () => `${metadata.start_date?.label}${metadata.start_date?.required ? ' *' : ''}`,
    [metadata.start_date]
  );
  const startTimeLabel = useMemo(
    () => `${getTimeLabel(metadata.start_date?.label)}${metadata.start_date?.required ? ' *' : ''}`,
    [metadata.start_date]
  );
  const endDateLabel = useMemo(
    () => `${metadata.end_date?.label}${metadata.end_date?.required ? ' *' : ''}`,
    [metadata.end_date]
  );
  const endTimeLabel = useMemo(
    () => `${getTimeLabel(metadata.end_date?.label)}${metadata.end_date?.required ? ' *' : ''}`,
    [metadata.end_date]
  );

  const startDateProps = useMemo(
    () => ({
      ...PICKER_PROPS,
      clearable: !metadata.start_date?.required,
      disabled: metadata.start_date?.read_only || readOnly,
      errorText: startDateError,
      floatingLabelText: startDateLabel,
      id: 'start_date',
      maxDate: endDateValue,
      name: 'start_date',
      onChange: onStartDateChange,
      value: startDateValue
    }),
    [endDateValue, metadata.start_date, onStartDateChange, readOnly, startDateError, startDateLabel, startDateValue]
  );

  const endDateProps = useMemo(
    () => ({
      ...PICKER_PROPS,
      clearable: !metadata.end_date?.required,
      disabled: metadata.end_date?.read_only || readOnly,
      errorText: endDateError,
      floatingLabelText: endDateLabel,
      id: 'end_date',
      minDate: startDateValue,
      name: 'end_date',
      onChange: onEndDateChange,
      value: endDateValue
    }),
    [endDateError, endDateLabel, endDateValue, metadata.end_date, onEndDateChange, readOnly, startDateValue]
  );

  const startTimeProps = useMemo(
    () => ({
      ...PICKER_PROPS,
      disabled: metadata.start_date?.read_only || readOnly,
      errorText: startTimeError,
      floatingLabelText: startTimeLabel,
      fullWidth: true,
      id: 'start_time',
      name: 'start_time',
      onChange: onStartTimeChange,
      value: startTimeValue,
      format: metadata?.start_date?.format
    }),
    [metadata.start_date, onStartTimeChange, readOnly, startTimeValue, startTimeError, startTimeLabel]
  );

  const endTimeProps = useMemo(
    () => ({
      ...PICKER_PROPS,
      disabled: metadata.end_date?.read_only || readOnly,
      errorText: endTimeError,
      floatingLabelText: endTimeLabel,
      fullWidth: true,
      id: 'end_time',
      name: 'end_time',
      onChange: onEndTimeChange,
      value: endTimeValue,
      format: metadata?.end_date?.format
    }),
    [endTimeValue, endTimeError, endTimeLabel, metadata.end_date, onEndTimeChange, readOnly]
  );

  const checkbox = useMemo(() => (
    <div
      styleName={metadata.all_day?.style || 'col100'}
      style={{ margin: '0.5rem 0 0 2rem', paddingBottom: '0.5rem' }}
    >
      <Checkbox
        checked={data.all_day}
        disabled={metadata.all_day?.read_only || readOnly}
        name="all_day"
        label={metadata.all_day?.label}
        onChange={onCheck}
        size="small"
      />
    </div>
  ), [data.all_day, metadata.all_day, onCheck, readOnly]);

  const fullStyle = { ...detailEdit.columnStyles.fullDatetime };
  if (metadata.start_date.style === 'fullDatetime' && metadata.end_date.style === 'fullDatetime') {
    return (
      <div>
        {metadata.start_date.title && <DrawerSectionTitle tab={metadata.start_date.title} />}
        <FormattedDatePicker {...startDateProps} fullWidth style={{...fullStyle}} styleName="input-field" />
        <MomentTimePicker {...startTimeProps} fullWidth style={{...fullStyle}} styleName="input-field" />
        {metadata.end_date.title && <DrawerSectionTitle tab={metadata.end_date.title} />}
        <FormattedDatePicker {...endDateProps} fullWidth style={{...fullStyle}} styleName="input-field" />
        <MomentTimePicker {...endTimeProps} fullWidth style={{...fullStyle}} styleName="input-field" />
      </div>
    );
  }
  if (Boolean(metadata.all_day?.linebreak)) {
    return (
      <div>
        <FormattedDatePicker {...startDateProps} fullWidth style={{...detailEdit.columnStyles.DateTimeRange}} />
        {timeVisible && <MomentTimePicker {...startTimeProps} style={{...detailEdit.columnStyles.DateTimeRange}} />}
        <FormattedDatePicker {...endDateProps} fullWidth style={{...detailEdit.columnStyles.DateTimeRange}} />
        {timeVisible && <MomentTimePicker {...endTimeProps} style={{...detailEdit.columnStyles.DateTimeRange}} />}
        {fieldNames.includes('all_day') && checkbox}
      </div>
    );
  }
  return (
    <div>
      <FormattedDatePicker {...startDateProps} fullWidth={!timeVisible} style={{...startStyle}} styleName="input-field" />
      {timeVisible && <MomentTimePicker {...startTimeProps} style={{...detailEdit.columnStyles.startDateRangeTime}} styleName="input-field" />}
      {timeVisible &&
        <MomentTimePicker
          {...endTimeProps}
          style={{...detailEdit.columnStyles.endDateRangeTime}}
          styleName="input-field"
        />
      }
      <FormattedDatePicker {...endDateProps} fullWidth={!timeVisible} style={{...endStyle}} styleName="input-field" />
      {fieldNames.includes('all_day') && checkbox}
    </div>
  );
};

DateRange.propTypes = {
  fieldNames: PropTypes.array,
  isPublic: PropTypes.bool,
  readOnly: PropTypes.bool
};

export default memo(DateRange);
